import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Layout, RequireAuth, RequirePermission, useHome } from './components/Layout';
import { ToastProvider } from './components/ui';
import { ApiError } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { Categories } from './pages/catalog/Categories';
import { Inventory } from './pages/catalog/Inventory';
import { ProductEdit } from './pages/catalog/ProductEdit';
import { Products } from './pages/catalog/Products';
import { Dashboard } from './pages/Dashboard';
import { AcceptInvite, Login } from './pages/Login';
import { Coupons } from './pages/marketing/Coupons';
import { Notifications } from './pages/marketing/Notifications';
import { Reviews } from './pages/catalog/Reviews';
import { Banners, Pages } from './pages/marketing/Content';
import { OrderDetail } from './pages/orders/OrderDetail';
import { OrdersList } from './pages/orders/OrdersList';
import { CustomerDetail, Customers } from './pages/sales/Customers';
import { Employees } from './pages/sales/Employees';
import { PaymentDetail, Payments, Webhooks } from './pages/sales/Payments';
import { Audit } from './pages/setup/Audit';
import { Settings } from './pages/setup/Settings';
import { Shipping } from './pages/setup/Shipping';
import { Team } from './pages/setup/Team';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2 },
    mutations: { retry: false },
  },
});

const P = (perm: string, el: ReactNode) => <RequirePermission perm={perm}>{el}</RequirePermission>;

function Home() {
  const { can } = useAuth();
  const home = useHome();
  return can('dashboard.read') ? <Dashboard /> : <Navigate to={home} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<Home />} />
        <Route path="orders" element={P('orders.read', <OrdersList />)} />
        <Route path="orders/:id" element={P('orders.read', <OrderDetail />)} />
        <Route path="payments" element={P('payments.read', <Payments />)} />
        <Route path="payments/:id" element={P('payments.read', <PaymentDetail />)} />
        <Route path="customers" element={P('customers.read', <Customers />)} />
        <Route path="customers/:id" element={P('customers.read', <CustomerDetail />)} />
        <Route path="employees" element={P('employees.manage', <Employees />)} />
        <Route path="products" element={P('products.read', <Products />)} />
        <Route path="products/new" element={P('products.create', <ProductEdit />)} />
        <Route path="products/:id" element={P('products.read', <ProductEdit />)} />
        <Route path="categories" element={P('products.read', <Categories />)} />
        <Route path="inventory" element={P('inventory.read', <Inventory />)} />
        <Route path="reviews" element={P('reviews.moderate', <Reviews />)} />
        <Route path="notifications" element={P('notifications.templates.manage', <Notifications />)} />
        <Route path="coupons" element={P('coupons.manage', <Coupons />)} />
        <Route path="banners" element={P('content.manage', <Banners />)} />
        <Route path="pages" element={P('content.manage', <Pages />)} />
        <Route path="shipping" element={P('shipping.read', <Shipping />)} />
        <Route path="settings" element={P('settings.manage', <Settings />)} />
        <Route path="team" element={P('users.manage', <Team />)} />
        <Route path="audit" element={P('audit.read', <Audit />)} />
        <Route path="webhooks" element={P('webhooks.manage', <Webhooks />)} />
        <Route path="*" element={<div className="py-16 text-center"><h1 className="font-heading text-3xl text-forest-900">Page not found</h1></div>} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter><AppRoutes /></BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
